// src/pages/JwtTestPage.tsx
import React, { useState } from 'react';
import { 
  Container, Typography, Box, Button, Alert, 
  CircularProgress, Divider, Chip, Tabs, Tab
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../api/config';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import CodeIcon from '@mui/icons-material/Code';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`jwt-test-tabpanel-${index}`}
      aria-labelledby={`jwt-test-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const JwtTestPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  
  // Common state
  const [loading, setLoading] = useState<{[key: string]: boolean}>({
    publicExp: false,
    protectedExp: false,
    adminExp: false,
    publicSkill: false,
    protectedSkill: false,
    adminSkill: false,
    viewer: false
  });
  const [error, setError] = useState<{[key: string]: string | null}>({
    publicExp: null,
    protectedExp: null,
    adminExp: null,
    publicSkill: null,
    protectedSkill: null,
    adminSkill: null,
    viewer: null
  });
  const [success, setSuccess] = useState<{[key: string]: boolean}>({
    publicExp: false,
    protectedExp: false,
    adminExp: false,
    publicSkill: false,
    protectedSkill: false,
    adminSkill: false,
    viewer: false
  });
  
  // Data state
  const [publicExpData, setPublicExpData] = useState<any>(null);
  const [protectedExpData, setProtectedExpData] = useState<any>(null);
  const [adminExpData, setAdminExpData] = useState<any>(null);
  const [publicSkillData, setPublicSkillData] = useState<any>(null);
  const [protectedSkillData, setProtectedSkillData] = useState<any>(null);
  const [adminSkillData, setAdminSkillData] = useState<any>(null);
  const [viewerData, setViewerData] = useState<any>(null);

  // Check user roles
  const isAdmin = user?.role === 'admin';
  const isEditor = user?.role === 'editor' || isAdmin;
  const isViewer = isAuthenticated;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // EXPERIENCE TESTS
  
  // Test public experience endpoint
  const testPublicExperienceEndpoint = async () => {
    setLoading(prev => ({ ...prev, publicExp: true }));
    setError(prev => ({ ...prev, publicExp: null }));
    setSuccess(prev => ({ ...prev, publicExp: false }));
    
    try {
      const response = await api.get('/experience/api/experiences');
      setPublicExpData(response.data);
      setSuccess(prev => ({ ...prev, publicExp: true }));
    } catch (err: any) {
      setError(prev => ({ 
        ...prev, 
        publicExp: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, publicExp: false }));
    }
  };

  // Test protected experience endpoint (editor/admin)
  const testProtectedExperienceEndpoint = async () => {
    setLoading(prev => ({ ...prev, protectedExp: true }));
    setError(prev => ({ ...prev, protectedExp: null }));
    setSuccess(prev => ({ ...prev, protectedExp: false }));
    
    try {
      // Try to create a test experience (requires editor/admin)
      const testData = {
        title: "Test Experience",
        company: "Test Company",
        location: "Test Location",
        date_start: new Date().toISOString().split('T')[0],
        is_current: true
      };
      
      const response = await api.post('/experience/api/experiences', testData);
      setProtectedExpData(response.data);
      setSuccess(prev => ({ ...prev, protectedExp: true }));
    } catch (err: any) {
      setError(prev => ({ 
        ...prev, 
        protectedExp: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, protectedExp: false }));
    }
  };

  // Test admin-only experience endpoint
  const testAdminExperienceEndpoint = async () => {
    setLoading(prev => ({ ...prev, adminExp: true }));
    setError(prev => ({ ...prev, adminExp: null }));
    setSuccess(prev => ({ ...prev, adminExp: false }));
    
    try {
      console.log("Starting admin experience endpoint test");
      
      // First create a test experience to delete
      console.log("Creating test experience...");
      const createResponse = await api.post('/experience/api/experiences', {
        title: "Test Experience to Delete",
        company: "Test Company",
        location: "Test Location",
        date_start: new Date().toISOString().split('T')[0],
        is_current: true
      });
      
      console.log("Create response:", createResponse);
      console.log("Create response data:", createResponse.data);
      
      if (!createResponse.data || !createResponse.data.data || !createResponse.data.data.id) {
        throw new Error("Failed to get ID from created experience");
      }
      
      const experienceId = createResponse.data.data.id;
      console.log("Experience ID to delete:", experienceId);
      
      // Then try to delete it (admin only)
      console.log(`Sending DELETE request to /experience/api/experiences/${experienceId}`);
      const response = await api.delete(`/experience/api/experiences/${experienceId}`);
      console.log("Delete response:", response);
      console.log("Delete response data:", response.data);
      
      // Verify deletion by trying to fetch the deleted item
      console.log("Verifying deletion...");
      try {
        const verifyResponse = await api.get(`/experience/api/experiences/${experienceId}`);
        console.log("Verification response:", verifyResponse.data);
        if (verifyResponse.data && verifyResponse.data.data) {
          console.warn("Experience still exists after deletion!");
          setError(prev => ({ ...prev, adminExp: "Deletion reported success but item still exists" }));
        } else {
          console.log("Experience successfully deleted");
          setSuccess(prev => ({ ...prev, adminExp: true }));
        }
      } catch (verifyErr) {
        console.log("Verification confirms deletion - experience not found");
        setSuccess(prev => ({ ...prev, adminExp: true }));
      }
      
      setAdminExpData(response.data);
    } catch (err: any) {
      console.error("Admin test error:", err);
      console.error("Error details:", err.response?.data);
      setError(prev => ({ 
        ...prev, 
        adminExp: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, adminExp: false }));
    }
  };

  // SKILL TESTS
  
  // Test public skill endpoint
  const testPublicSkillEndpoint = async () => {
    setLoading(prev => ({ ...prev, publicSkill: true }));
    setError(prev => ({ ...prev, publicSkill: null }));
    setSuccess(prev => ({ ...prev, publicSkill: false }));
    
    try {
      const response = await api.get('/skills/api/skills');
      setPublicSkillData(response.data);
      setSuccess(prev => ({ ...prev, publicSkill: true }));
    } catch (err: any) {
      setError(prev => ({ 
        ...prev, 
        publicSkill: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, publicSkill: false }));
    }
  };

  // Test protected skill endpoint (editor/admin)
  const testProtectedSkillEndpoint = async () => {
    setLoading(prev => ({ ...prev, protectedSkill: true }));
    setError(prev => ({ ...prev, protectedSkill: null }));
    setSuccess(prev => ({ ...prev, protectedSkill: false }));
    
    try {
      // Try to create a test skill (requires editor/admin)
      const testData = {
        name: "Test Skill",
        category: "Programming",
        proficiency: 75,
        description: "A test skill created via API"
      };
      
      const response = await api.post('/skills/api/skills', testData);
      setProtectedSkillData(response.data);
      setSuccess(prev => ({ ...prev, protectedSkill: true }));
    } catch (err: any) {
      setError(prev => ({ 
        ...prev, 
        protectedSkill: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, protectedSkill: false }));
    }
  };

  // Test admin-only skill endpoint
  const testAdminSkillEndpoint = async () => {
    setLoading(prev => ({ ...prev, adminSkill: true }));
    setError(prev => ({ ...prev, adminSkill: null }));
    setSuccess(prev => ({ ...prev, adminSkill: false }));
    
    try {
      console.log("Starting admin skill endpoint test");
      
      // First create a test skill to delete
      console.log("Creating test skill...");
      const createResponse = await api.post('/skills/api/skills', {
        name: "Test Skill to Delete",
        category: "Programming",
        proficiency: 50,
        description: "This skill will be deleted"
      });
      
      console.log("Create response:", createResponse);
      console.log("Create response data:", createResponse.data);
      
      if (!createResponse.data || !createResponse.data.data || !createResponse.data.data.id) {
        throw new Error("Failed to get ID from created skill");
      }
      
      const skillId = createResponse.data.data.id;
      console.log("Skill ID to delete:", skillId);
      
      // Then try to delete it (admin only)
      console.log(`Sending DELETE request to /skills/api/skills/${skillId}`);
      const response = await api.delete(`/skills/api/skills/${skillId}`);
      console.log("Delete response:", response);
      console.log("Delete response data:", response.data);
      
      // Verify deletion by trying to fetch the deleted item
      console.log("Verifying deletion...");
      try {
        const verifyResponse = await api.get(`/skills/api/skills/${skillId}`);
        console.log("Verification response:", verifyResponse.data);
        if (verifyResponse.data && verifyResponse.data.data) {
          console.warn("Skill still exists after deletion!");
          setError(prev => ({ ...prev, adminSkill: "Deletion reported success but item still exists" }));
        } else {
          console.log("Skill successfully deleted");
          setSuccess(prev => ({ ...prev, adminSkill: true }));
        }
      } catch (verifyErr) {
        console.log("Verification confirms deletion - skill not found");
        setSuccess(prev => ({ ...prev, adminSkill: true }));
      }
      
      setAdminSkillData(response.data);
    } catch (err: any) {
      console.error("Admin skill test error:", err);
      console.error("Error details:", err.response?.data);
      setError(prev => ({ 
        ...prev, 
        adminSkill: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, adminSkill: false }));
    }
  };

  // Test viewer-only endpoint (all authenticated users)
  const testViewerEndpoint = async () => {
    setLoading(prev => ({ ...prev, viewer: true }));
    setError(prev => ({ ...prev, viewer: null }));
    setSuccess(prev => ({ ...prev, viewer: false }));
    
    try {
      // This could be any endpoint that requires just authentication
      const response = await api.get('/api/test/jwt/protected');
      setViewerData(response.data);
      setSuccess(prev => ({ ...prev, viewer: true }));
    } catch (err: any) {
      setError(prev => ({ 
        ...prev, 
        viewer: err.response?.data?.message || err.message || 'Unknown error' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, viewer: false }));
    }
  };

  // Get token info
  const getTokenInfo = () => {
    const token = localStorage.getItem('token');
    if (!token) return "No token found";
    
    try {
      // Decode JWT (without verification - just to see payload)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      return "Invalid token format";
    }
  };

  // Get role badge
  const getRoleBadge = () => {
    if (!user) return null;
    
    switch(user.role) {
      case 'admin':
        return <Chip icon={<AdminPanelSettingsIcon />} label="Admin" color="error" />;
      case 'editor':
        return <Chip icon={<LockIcon />} label="Editor" color="primary" />;
      case 'viewer':
        return <Chip icon={<PersonIcon />} label="Viewer" color="success" />;
      default:
        return <Chip label={user.role} />;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ p: 4, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          JWT & Role-Based Access Testing
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>
              {isAuthenticated 
                ? `Authenticated as ${user?.username}` 
                : "Not authenticated"}
            </Typography>
            {isAuthenticated && getRoleBadge()}
          </Box>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            JWT Token Information
          </Typography>
          <Box 
            component="pre" 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '200px'
            }}
          >
            {JSON.stringify(getTokenInfo(), null, 2)}
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Viewer Test (Common for all resources) */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <PersonIcon color="success" />
              <Typography variant="h6">
                Test Authenticated Endpoint
              </Typography>
            </Box>
            
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Tests access to an endpoint that requires authentication (any role).
            </Typography>
            
            <Button 
              variant="contained" 
              onClick={testViewerEndpoint}
              disabled={loading.viewer || !isViewer}
              sx={{ mb: 2 }}
              color="success"
            >
              {loading.viewer ? <CircularProgress size={24} /> : 'Test Authentication'}
            </Button>
            
            {!isViewer && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You need to be authenticated to test this endpoint
              </Alert>
            )}
            
            {error.viewer && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.viewer}
              </Alert>
            )}
            
            {success.viewer && !error.viewer && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully accessed authenticated endpoint
              </Alert>
            )}
            
            {viewerData && (
              <Box 
                component="pre" 
                sx={{ 
                  p: 2, 
                  bgcolor: '#f5f5f5', 
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '200px'
                }}
              >
                {JSON.stringify(viewerData, null, 2)}
              </Box>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Resource-specific tests with tabs */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="resource test tabs"
            >
              <Tab 
                label="Experience Tests" 
                id="jwt-test-tab-0" 
                aria-controls="jwt-test-tabpanel-0" 
                icon={<WorkIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Skill Tests" 
                id="jwt-test-tab-1" 
                aria-controls="jwt-test-tabpanel-1"
                icon={<CodeIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          
          {/* Experience Tests */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Public Experience Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockOpenIcon color="success" />
                  <Typography variant="h6">
                    Test Public Experience Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to a public experience endpoint that doesn't require authentication.
                </Typography>
                
                <Button 
                  variant="contained" 
                  onClick={testPublicExperienceEndpoint}
                  disabled={loading.publicExp}
                  sx={{ mb: 2 }}
                >
                  {loading.publicExp ? <CircularProgress size={24} /> : 'Test Public Access'}
                </Button>
                
                {error.publicExp && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.publicExp}
                  </Alert>
                )}
                
                {success.publicExp && !error.publicExp && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed public experience endpoint
                  </Alert>
                )}
                
                {publicExpData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(publicExpData, null, 2)}
                  </Box>
                )}
              </Box>
              
              {/* Editor Experience Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockIcon color="primary" />
                  <Typography variant="h6">
                    Test Editor Experience Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to an experience endpoint that requires editor or admin privileges.
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={testProtectedExperienceEndpoint}
                  disabled={loading.protectedExp || !isEditor}
                  sx={{ mb: 2 }}
                >
                  {loading.protectedExp ? <CircularProgress size={24} /> : 'Test Editor Access'}
                </Button>
                
                {!isEditor && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You need editor or admin privileges to test this endpoint
                  </Alert>
                )}
                
                {error.protectedExp && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.protectedExp}
                  </Alert>
                )}
                
                {success.protectedExp && !error.protectedExp && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed editor-protected experience endpoint
                  </Alert>
                )}
                
                {protectedExpData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(protectedExpData, null, 2)}
                  </Box>
                )}
              </Box>
              
              {/* Admin Experience Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <AdminPanelSettingsIcon color="error" />
                  <Typography variant="h6">
                    Test Admin Experience Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to an experience endpoint that requires admin privileges.
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={testAdminExperienceEndpoint}
                  disabled={loading.adminExp || !isAdmin}
                  sx={{ mb: 2 }}
                >
                  {loading.adminExp ? <CircularProgress size={24} /> : 'Test Admin Access'}
                </Button>
                
                {!isAdmin && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You need admin privileges to test this endpoint
                  </Alert>
                )}
                
                {error.adminExp && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.adminExp}
                  </Alert>
                )}
                
                {success.adminExp && !error.adminExp && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed admin-protected experience endpoint
                  </Alert>
                )}
                
                {adminExpData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(adminExpData, null, 2)}
                  </Box>
                )}
              </Box>
            </Box>
          </TabPanel>
          
          {/* Skill Tests */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Public Skill Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockOpenIcon color="success" />
                  <Typography variant="h6">
                    Test Public Skill Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to a public skill endpoint that doesn't require authentication.
                </Typography>
                
                <Button 
                  variant="contained" 
                  onClick={testPublicSkillEndpoint}
                  disabled={loading.publicSkill}
                  sx={{ mb: 2 }}
                >
                  {loading.publicSkill ? <CircularProgress size={24} /> : 'Test Public Access'}
                </Button>
                
                {error.publicSkill && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.publicSkill}
                  </Alert>
                )}
                
                {success.publicSkill && !error.publicSkill && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed public skill endpoint
                  </Alert>
                )}
                
                {publicSkillData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(publicSkillData, null, 2)}
                  </Box>
                )}
              </Box>
              
              {/* Editor Skill Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockIcon color="primary" />
                  <Typography variant="h6">
                    Test Editor Skill Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to a skill endpoint that requires editor or admin privileges.
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={testProtectedSkillEndpoint}
                  disabled={loading.protectedSkill || !isEditor}
                  sx={{ mb: 2 }}
                >
                  {loading.protectedSkill ? <CircularProgress size={24} /> : 'Test Editor Access'}
                </Button>
                
                {!isEditor && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You need editor or admin privileges to test this endpoint
                  </Alert>
                )}
                
                {error.protectedSkill && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.protectedSkill}
                  </Alert>
                )}
                
                {success.protectedSkill && !error.protectedSkill && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed editor-protected skill endpoint
                  </Alert>
                )}
                
                {protectedSkillData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(protectedSkillData, null, 2)}
                  </Box>
                )}
              </Box>
              
               {/* Admin Skill Test */}
              <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <AdminPanelSettingsIcon color="error" />
                  <Typography variant="h6">
                    Test Admin Skill Endpoint
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tests access to a skill endpoint that requires admin privileges.
                </Typography>
                
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={testAdminSkillEndpoint}
                  disabled={loading.adminSkill || !isAdmin}
                  sx={{ mb: 2 }}
                >
                  {loading.adminSkill ? <CircularProgress size={24} /> : 'Test Admin Access'}
                </Button>
                
                {!isAdmin && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You need admin privileges to test this endpoint
                  </Alert>
                )}
                
                {error.adminSkill && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error.adminSkill}
                  </Alert>
                )}
                
                {success.adminSkill && !error.adminSkill && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully accessed admin-protected skill endpoint
                  </Alert>
                )}
                
                {adminSkillData && (
                  <Box 
                    component="pre" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(adminSkillData, null, 2)}
                  </Box>
                )}
              </Box>
            </Box>
          </TabPanel>
        </Box>
      </Box>
    </Container>
  );
};

export default JwtTestPage;
